import { generatePath, Link } from 'react-router-dom'

import { MultisigGroup } from '@/api'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatAddress } from '@/helpers/formatters'
import { RoutePaths } from '@/routes'
import UiAddressCopy from '@/ui/UiAddressCopy'
import { UiBadge } from '@/ui/UiBadge'
import { UiCard } from '@/ui/UiCard'

interface GroupCardProps {
  group: MultisigGroup
}

export default function GroupCard({ group }: GroupCardProps) {
  const { address } = useKeplrWallet()
  const membersCount = group.members.length
  const firstTwoMembers = group.members.slice(0, 2)
  const remainingCount = Math.max(0, membersCount - firstTwoMembers.length)

  const isMember =
    address &&
    group.members.some(member => member.toLowerCase() === address.toLowerCase())

  const path = generatePath(RoutePaths.MultisigGroup, {
    account: group.account,
  })

  return (
    <Link to={path} className='block'>
      <UiCard className='hover:border-border/90 hover:bg-card/45 transition-colors'>
        <div className='space-y-3 p-6'>
          <div className='flex flex-col gap-1'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-muted-foreground text-xs'>Account</p>
              {isMember && (
                <UiBadge
                  variant='default'
                  size='sm'
                  className='bg-primary/10 text-primary border-primary/20'
                >
                  Member
                </UiBadge>
              )}
            </div>
            <UiAddressCopy
              start={20}
              end={10}
              address={group.account}
              className='font-mono'
              showFull={false}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1'>
              <p className='text-muted-foreground text-xs'>Members</p>
              <p className='text-sm'>{membersCount}</p>
            </div>
            <div className='flex flex-col gap-1'>
              <p className='text-muted-foreground text-xs'>Threshold</p>
              <p className='text-sm'>{group.threshold}</p>
            </div>
          </div>

          <div>
            <p className='text-muted-foreground text-xs'>Members preview</p>
            <div className='mt-2 flex flex-wrap gap-2'>
              {firstTwoMembers.map(memberAddress => (
                <UiBadge
                  key={memberAddress}
                  variant='outline'
                  size='sm'
                  className='font-mono'
                >
                  {formatAddress(memberAddress, 20, 10)}
                </UiBadge>
              ))}
              {remainingCount > 0 && (
                <UiBadge
                  variant='secondary'
                  size='sm'
                  className='text-muted-foreground'
                >
                  +{remainingCount} more
                </UiBadge>
              )}
            </div>
          </div>
        </div>
      </UiCard>
    </Link>
  )
}
